import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Mail, Send } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { PageContainer } from "@/layouts/PageContainer";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { DataTable } from "@/components/tables/DataTable";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { getApiErrorMessage } from "@/api/client";
import LiveChat from "@/pages/Support/LiveChat";
import {
  SUPPORT_GROUPS,
  chatCounts,
  defaultGroupKey,
  groupCounts,
  sourceCounts,
  statusCount,
  visibleGroups,
  type SupportGroupKey,
  type SupportSource,
  type SupportStatusFilter,
} from "@/features/support/categories";
import {
  useReplyToTicket,
  useSupportRealtimeSync,
  useSupportSummary,
  useTicket,
  useTicketList,
  useUpdateTicket,
} from "@/features/support/useSupport";
import { toast } from "@/store/toast.store";
import { cn } from "@/utils/cn";
import { formatDate, formatDateTime } from "@/utils/format";
import type { SupportTicket, TicketPriority, TicketStatus } from "@/types/domain";

const PAGE_SIZE = 20;

const STATUS_TONE: Record<TicketStatus, BadgeTone> = { OPEN: "gold", WAITING: "neutral", RESOLVED: "teal" };
const STATUS_LABEL: Record<TicketStatus, string> = {
  OPEN: "Unresolved",
  WAITING: "In progress",
  RESOLVED: "Resolved",
};
const CATEGORY_LABEL: Record<string, string> = {
  UNSAFE_DRIVING: "Unsafe driving",
  RUDE_BEHAVIOUR: "Rude behaviour",
  OVERCHARGED: "Overcharged",
  VEHICLE_CONDITION: "Vehicle condition",
  OTHER: "Other",
  LOST_ITEM: "Lost item",
  PAYMENT_REFUND: "Payments & refunds",
  ACCOUNT: "Account & profile",
  APP_BUG: "App bug",
  GENERAL: "Live chat",
  DRV_EARNINGS: "Earnings & withdrawals",
  DRV_DOCUMENTS: "Documents & vehicle", // topic switched off for now; kept so any ticket already raised still shows its name
  DRV_TRIP_FARE: "Trip & fare issue",
  DRV_REPORT_RIDER: "Report a rider",
  DRV_ACCOUNT: "Account & profile",
  DRV_APP_BUG: "App issue",
  DRV_OTHER: "Other",
  DRV_CHAT: "Live chat",
};

function categoryLabel(t: SupportTicket) {
  if (t.isSos) return "SOS";
  return (t.category && CATEGORY_LABEL[t.category]) || "Ticket";
}

const PRIORITY_OPTIONS: { value: "" | TicketPriority; label: string }[] = [
  { value: "", label: "All priorities" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

/** The person who raised a ticket: the driver on a driver ticket, the rider otherwise. Older backends only send the rider fields. */
const requesterOf = (t: SupportTicket) =>
  t.requester ?? { type: "RIDER" as const, name: t.customerName ?? null, phone: t.customerPhone ?? null };

/** The short reference printed in the emails sent to the customer. */
const ticketRef = (id: string) => id.slice(-8).toUpperCase();

type SupportView = "tickets" | "chat";

/**
 * The Support page: tickets (email-style requests) and the Live chat inbox, switched with a toggle that
 * lives in the URL (`?view=chat`) so a chat can be linked to. Both are fed by the same support tickets and
 * the same realtime sync; live chat sits under /support so it needs no page permission of its own.
 */
export default function Support() {
  useSupportRealtimeSync();
  const [params, setParams] = useSearchParams();
  const view: SupportView = params.get("view") === "chat" ? "chat" : "tickets";
  const summary = useSupportSummary();
  const chats = chatCounts(summary.data);

  // Unanswered chats are the count that matters: a person is waiting on a reply, live.
  const viewOptions: SegmentedControlOption<SupportView>[] = [
    { value: "tickets", label: "Tickets" },
    { value: "chat", label: "Live chat", count: chats.unanswered, countTone: chats.unanswered > 0 ? "danger" : undefined },
  ];
  const viewSwitch = (
    <SegmentedControl
      options={viewOptions}
      value={view}
      onChange={(v) => setParams(v === "chat" ? { view: "chat" } : {}, { replace: true })}
      size="md"
    />
  );
  return view === "chat" ? <LiveChat viewSwitch={viewSwitch} /> : <TicketsView viewSwitch={viewSwitch} />;
}

function TicketsView({ viewSwitch }: { viewSwitch: ReactNode }) {
  const [source, setSource] = useState<SupportSource>("RIDER");
  const [groupKey, setGroupKey] = useState<SupportGroupKey>("ALL");
  const [status, setStatus] = useState<SupportStatusFilter>("OPEN");
  const [priority, setPriority] = useState<"" | TicketPriority>("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const summary = useSupportSummary();
  const group = SUPPORT_GROUPS.find((g) => g.key === groupKey && g.source === source) ?? SUPPORT_GROUPS.find((g) => g.key === defaultGroupKey(source)) ?? SUPPORT_GROUPS[0];
  const counts = groupCounts(summary.data, group);

  // Switching between riders and drivers only changes what is shown; no ticket is touched.
  function switchSource(next: SupportSource) {
    if (next === source) return;
    setSource(next);
    setGroupKey(defaultGroupKey(next));
    setPage(1);
    setSelectedId(null);
  }

  const query = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: status === "ALL" ? undefined : status,
      priority: priority || undefined,
      source,
      ...group.query,
    }),
    [page, status, priority, source, group],
  );
  const list = useTicketList(query);

  // The count on each side is its Unresolved tickets, in red while there are any.
  const sourceOptions: SegmentedControlOption<SupportSource>[] = (["RIDER", "DRIVER"] as const).map((s) => {
    const unresolved = sourceCounts(summary.data, s).open;
    return {
      value: s,
      label: s === "RIDER" ? "Riders" : "Drivers",
      count: unresolved,
      countTone: unresolved > 0 ? ("danger" as const) : undefined,
    };
  });

  const statusOptions: SegmentedControlOption<SupportStatusFilter>[] = [
    { value: "OPEN", label: "Unresolved", count: counts.open },
    { value: "WAITING", label: "In progress", count: counts.waiting },
    { value: "RESOLVED", label: "Resolved", count: counts.resolved },
    { value: "ALL", label: "All", count: statusCount(counts, "ALL") },
  ];

  const columns = useMemo<ColumnDef<SupportTicket, unknown>[]>(
    () => [
      {
        header: "Status",
        cell: ({ row }) => {
          const t = row.original;
          return (
            <div className="flex items-center gap-1.5">
              {t.isSos && <Badge tone="danger">SOS</Badge>}
              {t.priority === "HIGH" && !t.isSos && <Badge tone="danger">High</Badge>}
              <Badge tone={STATUS_TONE[t.status]}>{STATUS_LABEL[t.status]}</Badge>
            </div>
          );
        },
      },
      {
        header: "Subject",
        cell: ({ row }) => {
          const t = row.original;
          const last = t.messages.length > 0 ? t.messages[t.messages.length - 1].text : (t.detail ?? "");
          return (
            <div className="max-w-[22rem]">
              <p className={cn("truncate", t.status === "OPEN" ? "font-semibold" : "font-medium")}>{t.title}</p>
              <p className="truncate text-xs text-ink-faint">{last}</p>
            </div>
          );
        },
      },
      { header: "Topic", cell: ({ row }) => categoryLabel(row.original) },
      {
        header: source === "DRIVER" ? "Driver" : "Customer",
        cell: ({ row }) => {
          const who = requesterOf(row.original);
          return (
            <div>
              <p>{who.name ?? who.phone ?? "Unknown"}</p>
              {who.name && who.phone && <p className="text-xs text-ink-faint">{who.phone}</p>}
            </div>
          );
        },
      },
      ...(source === "DRIVER"
        ? [
            {
              header: "Vehicle",
              cell: ({ row }: { row: { original: SupportTicket } }) => (
                <span className="font-mono text-xs">{row.original.requesterVehicle?.registrationNumber ?? "—"}</span>
              ),
            } as ColumnDef<SupportTicket, unknown>,
          ]
        : []),
      {
        header: "Reply email",
        cell: ({ row }) =>
          row.original.contactEmail ? (
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-ink-faint" />
              {row.original.contactEmail}
            </span>
          ) : (
            <span className="text-ink-faint">— in-app only</span>
          ),
      },
      { header: "Ref", cell: ({ row }) => <span className="font-mono text-xs text-ink-muted">{ticketRef(row.original.id)}</span> },
      { header: "Msgs", accessorFn: (t) => t.messages.length },
      { header: "Updated", accessorFn: (t) => formatDateTime(t.updatedAt) },
    ],
    [source],
  );

  return (
    <PageContainer
      title="Support"
      description={
        source === "DRIVER"
          ? "Help requests from drivers: earnings, documents, trips, rider reports and app issues. Replies and resolutions are emailed to the driver."
          : "Customer help requests, driver reports and SOS alerts. Replies and resolutions are emailed to the customer."
      }
      actions={
        <div className="flex items-center gap-2">
          {viewSwitch}
          <div className="w-40">
            <Select
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value as "" | TicketPriority);
                setPage(1);
              }}
              aria-label="Priority"
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <SegmentedControl options={sourceOptions} value={source} onChange={switchSource} size="md" />
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div role="tablist" aria-label="Topic" className="flex flex-wrap gap-2">
          {visibleGroups(summary.data, source).map((g) => {
            const unresolved = statusCount(groupCounts(summary.data, g), "OPEN");
            const active = g.key === groupKey;
            return (
              <button
                key={g.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setGroupKey(g.key);
                  setPage(1);
                  setSelectedId(null);
                }}
                className={cn(
                  "flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? "border-teal-500 bg-teal-500 text-cream-50"
                    : "border-border bg-cream-50 text-ink hover:bg-cream-200",
                )}
              >
                {g.label}
                {unresolved > 0 && (
                  <span
                    title={`${unresolved} unresolved`}
                    className={cn(
                      "rounded-full px-1.5 text-[11px] font-semibold",
                      active ? "bg-teal-700/40 text-cream-50" : g.key === "SOS" ? "bg-danger-500 text-cream-50" : "bg-gold-100 text-gold-700",
                    )}
                  >
                    {unresolved}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl
            options={statusOptions}
            value={status}
            onChange={(s) => {
              setStatus(s);
              setPage(1);
            }}
          />
          <span className="text-xs text-ink-muted">
            {group.label}: {counts.open} unresolved · {counts.waiting} in progress · {counts.resolved} resolved
          </span>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={list.data?.items ?? []}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => list.refetch()}
          onRowClick={(t) => setSelectedId(t.id)}
          rowKey={(t) => t.id}
          emptyTitle="No tickets"
          emptyDescription="Nothing matches these filters."
        />
        {list.data && <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPageChange={setPage} />}
      </div>

      <TicketDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </PageContainer>
  );
}

/** One header line of an email: a label and its value. */
function MailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-14 shrink-0 text-ink-faint">{label}</span>
      <span className="min-w-0 break-words text-ink">{children}</span>
    </div>
  );
}

function TicketDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const ticket = useTicket(id);
  const reply = useReplyToTicket();
  const update = useUpdateTicket();
  const [text, setText] = useState("");
  const [resolving, setResolving] = useState(false);
  const [explanation, setExplanation] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const messageCount = ticket.data?.messages.length ?? 0;

  useEffect(() => {
    setText("");
    setResolving(false);
    setExplanation("");
  }, [id]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messageCount, id]);

  const t = ticket.data;
  const resolved = t?.status === "RESOLVED";
  const photos = (t?.attachments ?? []).filter((a) => !!a.url);
  const fromDriver = t?.source === "DRIVER";
  const personLabel = fromDriver ? "Driver" : "Customer";
  const who = t ? requesterOf(t) : { type: "RIDER" as const, name: null, phone: null };

  function send() {
    const body = text.trim();
    if (!t || !body) return;
    reply.mutate(
      { id: t.id, text: body },
      {
        onSuccess: (r) => {
          setText("");
          toast.success(r.emailedTo ? `Reply sent and emailed to ${r.emailedTo}` : "Reply sent");
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      },
    );
  }

  function change(payload: { status?: TicketStatus; priority?: TicketPriority; resolution?: string }, done: string) {
    if (!t) return;
    update.mutate(
      { id: t.id, ...payload },
      {
        onSuccess: (r) => {
          toast.success(r.emailedTo ? `${done} · emailed to ${r.emailedTo}` : done);
          setResolving(false);
          setExplanation("");
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      },
    );
  }

  return (
    <Drawer
      open={!!id}
      onClose={onClose}
      width={680}
      title={t ? t.title : "Ticket"}
      subtitle={t ? `Ref ${ticketRef(t.id)} · opened ${formatDateTime(t.createdAt)}` : undefined}
    >
      {ticket.isLoading && <LoadingState />}
      {(ticket.isError || (!ticket.isLoading && !t)) && <ErrorState error={ticket.error} onRetry={() => ticket.refetch()} />}
      {t && (
        <div className="flex flex-col gap-4">
          {/* status + actions */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              {t.isSos && <Badge tone="danger">SOS</Badge>}
              <Badge tone={STATUS_TONE[t.status]}>{STATUS_LABEL[t.status]}</Badge>
              <Badge>{categoryLabel(t)}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-32">
                <Select
                  value={t.priority}
                  onChange={(e) => change({ priority: e.target.value as TicketPriority }, "Priority updated")}
                  aria-label="Priority"
                >
                  <option value="HIGH">High</option>
                  <option value="NORMAL">Normal</option>
                  <option value="LOW">Low</option>
                </Select>
              </div>
              {resolved ? (
                <Button size="sm" variant="outline" loading={update.isPending} onClick={() => change({ status: "OPEN" }, "Ticket reopened as unresolved")}>
                  Reopen
                </Button>
              ) : (
                <>
                  {t.status === "OPEN" && (
                    <Button size="sm" variant="outline" loading={update.isPending} onClick={() => change({ status: "WAITING" }, "Marked in progress")}>
                      Mark in progress
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" disabled={resolving} onClick={() => setResolving(true)}>
                    Resolve
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* the report, as the email it is */}
          <section className="rounded-xl border border-border bg-cream-100">
            <div className="space-y-1 border-b border-border px-4 py-3">
              <MailField label="From">
                <span className="font-medium">{who.name ?? personLabel}</span>
                {t.contactEmail ? ` <${t.contactEmail}>` : who.phone ? ` · ${who.phone}` : ""}
              </MailField>
              <MailField label="To">Eyy Auto Support</MailField>
              <MailField label="Subject">{t.title}</MailField>
              {who.phone && t.contactEmail && <MailField label="Phone">{who.phone}</MailField>}
              {fromDriver && t.requesterVehicle && (
                <MailField label="Vehicle">
                  {t.requesterVehicle.registrationNumber} · {t.requesterVehicle.model}
                  {t.requesterVehicle.color ? ` · ${t.requesterVehicle.color}` : ""}
                  {t.requesterVehicle.permitExpiry ? ` · permit expires ${formatDate(t.requesterVehicle.permitExpiry)}` : ""}
                  {t.requesterVehicle.insuranceExpiry ? ` · insurance expires ${formatDate(t.requesterVehicle.insuranceExpiry)}` : ""}
                </MailField>
              )}
              {t.ride && (
                <MailField label="Ride">
                  #{t.ride.code} · {formatDateTime(t.ride.createdAt)} · {t.ride.status.replace(/_/g, " ").toLowerCase()} ·{" "}
                  {t.ride.pickupName} → {t.ride.dropName}
                </MailField>
              )}
              {fromDriver && t.reportedRider && (
                <MailField label="Rider">
                  {t.reportedRider.name ?? "Rider"} · {t.reportedRider.phone} <span className="text-ink-faint">(reported)</span>
                </MailField>
              )}
              {!fromDriver && (t.driverName || t.ride?.driverName) && (
                <MailField label="Driver">
                  {t.driverName ?? t.ride?.driverName}
                  {t.driverPhone ? ` · ${t.driverPhone}` : ""}
                  {t.ride?.vehicleNumber ? ` · Vehicle ${t.ride.vehicleNumber}` : ""}
                </MailField>
              )}
            </div>
            <div className="space-y-3 px-4 py-3">
              {t.detail && <p className="whitespace-pre-wrap break-words text-sm text-ink">{t.detail}</p>}
              {photos.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-ink">Attachments ({photos.length})</p>
                  <ul className="flex flex-wrap gap-2">
                    {photos.map((a, i) => (
                      <li key={a.key}>
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          title="Open full size"
                          className="block overflow-hidden rounded-lg border border-border bg-cream-200 hover:ring-2 hover:ring-teal-500"
                        >
                          <img src={a.url} alt={`Photo ${i + 1} from the ${personLabel.toLowerCase()}`} loading="lazy" className="h-20 w-20 object-cover" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          {/* correspondence */}
          {t.messages.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Correspondence ({t.messages.length})</p>
              {t.messages.map((m, i) => {
                const fromAdmin = m.senderRole === "ADMIN";
                return (
                  <article
                    key={m._id ?? i}
                    className={cn("rounded-xl border px-4 py-3", fromAdmin ? "border-teal-500/40 bg-teal-500/5" : "border-border bg-cream-100")}
                  >
                    <header className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
                      <span>
                        <span className="font-medium text-ink">
                          {fromAdmin ? (m.senderName ?? "Eyy Auto Support") : (m.senderName ?? who.name ?? personLabel)}
                        </span>{" "}
                        → {fromAdmin ? (t.contactEmail ?? `${personLabel.toLowerCase()} (in-app)`) : "Eyy Auto Support"}
                      </span>
                      <span>{formatDateTime(m.sentAt)}</span>
                    </header>
                    <p className="whitespace-pre-wrap break-words text-sm text-ink">{m.text}</p>
                  </article>
                );
              })}
            </div>
          )}

          {/* compose */}
          {resolved ? (
            <div className="space-y-1 rounded-xl border border-border bg-cream-100 px-4 py-3">
              <p className="text-sm text-ink-muted">This ticket is resolved. Reopen it to reply.</p>
              {t.resolution && <p className="text-xs text-ink">Resolution sent: {t.resolution}</p>}
            </div>
          ) : resolving ? (
            <section className="space-y-2 rounded-xl border border-border bg-cream-50 p-4">
              <MailField label="To">{t.contactEmail ?? `${personLabel} (in-app only — no email on this ticket)`}</MailField>
              <MailField label="Subject">Eyy Auto support: your request is resolved (ref {ticketRef(t.id)})</MailField>
              <label className="block pt-1 text-sm font-medium text-ink" htmlFor="resolution">
                What was done to resolve this?
              </label>
              <Textarea
                id="resolution"
                rows={4}
                autoFocus
                value={explanation}
                maxLength={1000}
                placeholder="e.g. We refunded ₹120 to your original payment method; it shows within 3–5 working days."
                onChange={(e) => setExplanation(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => setResolving(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  loading={update.isPending}
                  disabled={explanation.trim().length < 3}
                  onClick={() => change({ status: "RESOLVED", resolution: explanation.trim() }, "Ticket resolved")}
                >
                  Resolve{t.contactEmail ? " & email" : ""}
                </Button>
              </div>
            </section>
          ) : (
            <section className="space-y-2 rounded-xl border border-border bg-cream-50 p-4">
              <MailField label="To">{t.contactEmail ?? `${personLabel} (in-app only — no email on this ticket)`}</MailField>
              <MailField label="Subject">Re: {t.title}</MailField>
              <Textarea
                rows={5}
                value={text}
                maxLength={1000}
                placeholder={
                  t.contactEmail
                    ? `Write your reply — it is emailed to the ${personLabel.toLowerCase()} and shown in their app`
                    : `Write your reply — the ${personLabel.toLowerCase()} sees it in their app`
                }
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                }}
              />
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-ink-faint">Ctrl + Enter to send</span>
                <Button onClick={send} loading={reply.isPending} disabled={!text.trim()}>
                  <Send className="h-4 w-4" />
                  {t.contactEmail ? "Send email" : "Send reply"}
                </Button>
              </div>
            </section>
          )}
          <div ref={endRef} />
        </div>
      )}
    </Drawer>
  );
}
